import { VIEWER_URL } from '@vindr/services/constants/study.constant';
import keycloak from '@vindr/services/keycloak';
import { Modal, message } from 'antd';
import { MessageInstance } from 'antd/es/message/interface';
import { ModalStaticFunctions } from 'antd/es/modal/confirm';
import { Dispatch, createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useImmerReducer } from 'use-immer';
import { GlobalAction, GlobalState, globalInitialState, globalReducer } from './globalSetting';

export type setIframe = [
  { open: boolean; url: string },
  (study_instance_uid: string, modality?: string | string[]) => void,
  () => void,
  (study_instance_uid: string, isOpen?: boolean) => void
];

export interface GlobalContextProps {
  keycloak: typeof keycloak;
  message: MessageInstance;
  modal: Omit<ModalStaticFunctions, 'warn'>;
  iframe: setIframe;
  globalSetting: GlobalState;
  dispatchGlobalSetting: Dispatch<GlobalAction>;
}

const GlobalContext = createContext<GlobalContextProps>({} as GlobalContextProps);

const useSetIframe = (): setIframe => {
  const [iframeState, setIframeState] = useState({
    open: false,
    url: null,
  });

  const setOpenIframe = (study_instance_uid: string, modality: string | string[] = null) => {
    if (!study_instance_uid) return setIframeState({ open: true, url: null });

    setIframeState({
      open: true,
      url:
        VIEWER_URL +
        `/${
          modality === 'SM' || modality.includes('SM') ? 'microscopy' : 'viewer'
        }/?StudyInstanceUIDs=${study_instance_uid}`,
    });
  };

  const setCloseIframe = () => {
    setIframeState({ open: false, url: null });
  };

  const setToggleIframe = (study_instance_uid: string, isOpen?: boolean) => {
    if (isOpen === true || (isOpen === undefined && iframeState.open)) {
      setCloseIframe();
    } else {
      setOpenIframe(study_instance_uid);
    }
  };

  return [iframeState, setOpenIframe, setCloseIframe, setToggleIframe];
};

export const GlobalProvider = ({ children }: { children: React.ReactNode }) => {
  const [keycloakState] = useState(() => keycloak);
  const [messageApi, messageContextHolder] = message.useMessage();
  const [modalApi, modalContextHolder] = Modal.useModal();
  const [globalSetting, dispatchGlobalSetting] = useImmerReducer(globalReducer, globalInitialState);
  const [iframeState, setOpenIframe, setCloseIframe, setToggleIframe] = useSetIframe();

  const memorizedValues: GlobalContextProps = useMemo(
    () => ({
      keycloak: keycloakState,
      message: messageApi,
      modal: modalApi,
      iframe: [iframeState, setOpenIframe, setCloseIframe, setToggleIframe],
      globalSetting,
      dispatchGlobalSetting,
    }),
    [
      keycloakState,
      messageApi,
      modalApi,
      iframeState,
      setOpenIframe,
      setCloseIframe,
      setToggleIframe,
      globalSetting,
      dispatchGlobalSetting,
    ]
  );

  return (
    <GlobalContext.Provider value={memorizedValues}>
      {messageContextHolder}
      {modalContextHolder}
      {children}
    </GlobalContext.Provider>
  );
};

export const useGlobalContext = () => {
  const context = useContext(GlobalContext);

  if (!context) {
    throw new Error('useGlobalContext must be used within a GlobalProvider');
  }

  return context;
};
